<script setup>
import {onActivated, onDeactivated, onMounted, reactive, ref, watch} from "vue";

defineOptions({name: 'OrdersPendingView'})
import {useOrderStore} from "../../store/orders.js";
import EmptyState from "../../components/global/EmptyState.vue";
import Pagination from "../../components/section/Pagination.vue";
import OrderRowItem from "../../components/orders/OrderRowItem.vue";
import SectionHeader from "../../components/section/SectionHeader.vue";
import {useModalStore} from "../../store/modal.js";
import {useToastrStore} from "../../store/toastr.js";
import {useAuthStore} from "../../store/auth.js";

const modalStore = useModalStore()
const store = useOrderStore()
const toastrStore = useToastrStore()
const authStore = useAuthStore()
const all_orders = ref()
const search = ref()
const orderStatusOptions = reactive([])
let searchDebounce

const orderSortOptions = reactive([
    {label: 'A to Z', value: 'desc'},
    {label: 'Z to A', value: 'asc'},
])

const orderParams = reactive({
    status: "pending",
    limit: 25,
    sort_direction: 'desc',
    start_date: null,
    end_date: null
})

const clearDateFilters = () => {
    orderParams.start_date = null
    orderParams.end_date = null
}

const clearOtherFilters = () => {
    orderParams.status = "pending"
    orderParams.sort_direction = 'desc'
}

const getOrders = async (page = 1) => {
    await store.getOrders({
        ...orderParams,
        page: page
    })
}
const exportOrders = async () => {
    try {
        await store.exportOrders({...orderParams})
        toastrStore.show("Export in progress — you’ll get a notification when it’s ready to download")
    } catch (e) {
        console.log(e)
    }
}

// Hands the live filters to the wizard so the send targets the same list the report would.
const openBulkSend = () => {
    modalStore.open('BulkSendWizard', {filters: {...orderParams}}, 'body', 'bottom-right')
}

const getOrderStats = async () => {
    await store.getOrderOverviewStats('pending')
}

const handleSearch = async (query) => {
    await store.searchOrders(query, null)
}

const changeStatus = (status) => {
    orderParams.status = status
    getOrders()
}

watch((search), async (value) => {
    clearTimeout(searchDebounce)
    if (value) {
        searchDebounce = setTimeout(async () => {
            handleSearch(value)
        }, 250)
    } else {
        await getOrders()
    }
})

onMounted(async () => {
    await store.getStatuses();
    [...store.pending_statuses].filter(s => {
        orderStatusOptions.push({
            label: s.replace('-', ' ').replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase()),
            value: s
        })
    })


})

onActivated(async () => {
    await getOrderStats()
    await getOrders()
    all_orders.value = store.orders.data
})

onDeactivated(() => {
    store.resetOrders()
})

</script>

<template>
    <main v-if="!store.orders">
        <empty-state icon="speechbubble-double" heading="Loading orders." content="Give it sec, it's coming in a mo."/>
    </main>

    <main v-else-if="search || (store.orders.data.length > 0)">
        <section>
            <section-header :heading="`Pending orders.`">
                <div class="search-bar">
                    <brand-form-input label="Order number or client detail" variant="search" v-model="search"/>
                    <button class="mobile-filter-button"
                            @click.prevent="modalStore.open('OrderFilters', {}, null, 'center-bottom')">
                        <ah-icon name="filter-default"/>
                    </button>
                </div>
            </section-header>

            <div class="order-summary-section" v-if="store.orders.data.length">
                <p class="pending-order-total" @click="changeStatus('pending')">{{ store.orderStats.total }}</p>
                <template v-for="(stat, i) in store.orderStats.stats">
                    <div class="pending-order-stat" v-if="i < 4" @click="changeStatus(stat.status)">
                        <p class="pending-order-stat-amount">{{ stat.count }}</p>
                        <p class="pending-order-stat-label">{{ stat.status.replace(/-/g, ' ') }}</p>
                    </div>
                </template>
            </div>

            <div class="section-table" ref="scrollSection" v-if="store.orders.data.length">
                <order-row-item :order="o" v-for="o in store.orders.data"/>
            </div>

            <div class="search-empty-container" v-else-if="search && !store.orders.data.length">
                <h1>Sorry, there are no orders containing “{{ search }}”.</h1>
            </div>

            <div class="section-table-empty" v-else-if="!store.orders.data.length">
                <h1>No tickets match</h1>
            </div>
            <div class="section-footer" v-if="store.orders.data.length">
                <pagination :pagination="store.orders" @change-page="getOrders"/>
            </div>
        </section>

        <aside :class="['section-sidebar', {'section-sidebar-no-search':search && !store.orders.data.length}]">
            <div class="sidebar-header">
                <h2>Filters</h2>
                <p @click.prevent="clearOtherFilters">Clear</p>
            </div>
            <div class="sidebar-select">
                <brand-form-select variant="sidebar" label="Sorting." :options="orderSortOptions"
                                   v-model="orderParams.sort_direction"/>
            </div>
            <div class="sidebar-select">
                <brand-form-select variant="sidebar" label="Status." :options="orderStatusOptions"
                                   v-model="orderParams.status"/>
            </div>

            <div class="sidebar-header" title="Filters on when an order moved into its current status, not when it was placed">
                <h2>Status date</h2>
                <p @click.prevent="clearDateFilters">Clear</p>
            </div>
            <brand-form-input label="Start date." variant="date" v-model="orderParams.start_date"/>
            <brand-form-input label="End date." variant="date" v-model="orderParams.end_date"/>

            <div class="sidebar-footer">
                <div class="sidebar-footer-secondary-actions">
                    <brand-button as="button" variant="small" @click="exportOrders" title="Export to CSV">
                        <ah-icon name="arrow-in"/>
                    </brand-button>
                    <brand-button as="button" variant="small" @click="openBulkSend" title="Bulk WhatsApp these clients"
                                  v-if="authStore.canBulkSend">
                        <ah-icon name="speechbubble-double"/>
                    </brand-button>
                </div>
                <brand-button as="button" variant="primary" @click.prevent="getOrders(1)">Apply</brand-button>

            </div>
        </aside>
    </main>

    <main v-else>
        <empty-state icon="cart-default" heading="No pending orders."
                     content="Once an order is placed it will show up here."/>
    </main>
</template>

<style lang="scss">
.section-sidebar-no-search {
    opacity: .5;
}

.order-summary-section {
    display: grid;
    grid-template-columns: 10rem repeat(4, 1fr);
    margin-top: 2rem;
    grid-column-gap: 2rem;

    + .section-table {
        height: calc(100% - 24rem);
    }

    .pending-order-total {
        width: 10rem;
        height: 10rem;
        background: var(--color-tiffany-dark);
        @include font(3.6, 2.6, book);
        color: var(--color-white);
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        cursor: pointer;
    }

    .pending-order-stat {
        background: var(--color-white);
        padding: 1rem;
        border-radius: 8rem;
        display: flex;
        gap: 2rem;
        align-items: center;
        cursor: pointer;

        .pending-order-stat-amount {
            width: 8rem;
            height: 8rem;
            border-radius: 50%;
            background: var(--color-dew);
            @include font(2.4, 2.4, book);
            color: var(--thunder);
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .pending-order-stat-label {
            @include font(1.2, 1.2, black);
            text-transform: capitalize;
        }

        @include dark() {
            background: #192D39;
            color: var(--color-white);

            .pending-order-stat-amount {
                background: var(--color-white-20);
            }

            .pending-order-stat-label {
                color: var(--color-white);
            }
        }
    }

    @include mobile() {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, 1fr);
        grid-column-gap: 10px;
        grid-row-gap: 20px;
        .pending-order-total {
            grid-area: 1 / 1 / 2 / 3;
            border-radius: 8rem
        }
        .pending-order-stat {
            gap: 1.5rem;
            padding: 2rem;

            .pending-order-stat-amount {
                width: 5rem;
                height: 5rem;
                @include font(1.8, 1.8, book);
            }

            .pending-order-stat-label {
                width: calc(100% - 6.5rem);
                @include font(1.2, 1.7, black);
            }

            &:nth-child(1) {
                grid-area: 2 / 1 / 3 / 2;
            }

            &:nth-child(2) {
                grid-area: 2 / 2 / 3 / 3;
            }

            &:nth-child(3) {
                grid-area: 3 / 1 / 4 / 2;
            }

            &:nth-child(4) {
                grid-area: 3 / 2 / 4 / 3;
            }
        }

        .pending-order-total {
            width: 100%;
        }
    }
}

.search-empty-container {
    padding-top: 2rem;

    h1 {
        text-align: center;
    }
}

.search-bar {
    .mobile-filter-button {
        display: none;
    }

    @include mobile() {
        width: 100%;
        display: flex;
        gap: 1rem;

        .mobile-filter-button {
            width: 6rem;
            height: 6rem;
            background: var(--color-white);
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            border: none;
            outline: none;

            .icon {
                fill: var(--color-grey-hazy);
            }

            @include dark() {
                background: var(--color-darth-maul);
            }
        }

        .form-input {
            width: calc(100% - 8rem);
        }
    }
}
</style>

<style scoped lang="scss">
.search-bar {
    min-width: 34rem;

    @include mobile() {
        min-width: 0;
    }
}
</style>
